'use client'

import { ChevronDown12, ChevronUp12 } from '@vinted/monochrome-icons'
import { Button, Icon, Spacer, Text } from '@vinted/web-ui'
import { useState, useEffect, useMemo } from 'react'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import {
  getPrivacyPolicy,
  PRIVACY_CENTER_URL,
} from '@marketplace-web/user-agreements/privacy-center-data'

import BackToTopButton from '../../components/BackToTopButton'
import { useActiveTocLink } from '../../hooks'

const PrivacyPolicy = () => {
  const [isExpanded, setIsExpanded] = useState(false)

  const translate = useTranslate()

  const { fetch: fetchPrivacyPolicy, data, isLoading } = useFetch(getPrivacyPolicy)

  useEffect(() => {
    fetchPrivacyPolicy()
  }, [fetchPrivacyPolicy])

  useEffect(() => {
    const previousScrollBehavior = document.documentElement.style.scrollBehavior
    document.documentElement.style.scrollBehavior = 'smooth'

    return () => {
      document.documentElement.style.scrollBehavior = previousScrollBehavior
    }
  }, [])

  // Used to handle navigation from landing page where it leads to a specific section. After content loads, it scrolls to the section.
  useEffect(() => {
    if (!data) return

    const { hash } = window.location
    if (hash) {
      const sectionId = hash.substring(1)

      const section = document.getElementById(sectionId)
      if (section) {
        section.scrollIntoView()
      }
    }
  }, [data])

  useActiveTocLink({ data, isExpanded })

  const privacyPolicyContent = useMemo(() => {
    if (!data?.body) {
      return undefined
    }

    const updateDetailsElement = (detailsElement: HTMLDetailsElement) => {
      if (!isExpanded) {
        detailsElement.removeAttribute('open')

        return
      }

      detailsElement.setAttribute('open', 'true')
    }

    const parser = new DOMParser()
    const serializer = new XMLSerializer()
    const document = parser.parseFromString(data.body, 'text/html')
    const allDetailsElements = document.querySelectorAll('details')

    allDetailsElements.forEach(updateDetailsElement)

    return serializer.serializeToString(document)
  }, [data, isExpanded])

  const handleButtonClick = () => {
    setIsExpanded(currentState => !currentState)
  }

  const renderExpandAllButton = () => {
    if (!privacyPolicyContent) return null

    const buttonText = isExpanded
      ? translate('privacy_policy_details_button.collapse_all')
      : translate('privacy_policy_details_button.expand_all')

    return (
      <Button
        text={buttonText}
        styling="outlined"
        size="small"
        onClick={handleButtonClick}
        icon={<Icon name={isExpanded ? ChevronUp12 : ChevronDown12} color="primary-default" />}
        inline
      />
    )
  }

  const renderPolicyOverviewButton = () => {
    if (!privacyPolicyContent) return null

    return (
      <Button
        text={translate('privacy_policy.overview_button')}
        styling="filled"
        size="small"
        url={PRIVACY_CENTER_URL}
        inline
      />
    )
  }

  const renderTitleSection = () => {
    if (!privacyPolicyContent) return null

    return (
      <div className="policy__title">
        <h1>{translate('privacy_policy.title')}</h1>
        <p>{translate('privacy_policy.update_date')}</p>
        <div className="u-flexbox u-flex-wrap u-flex-justify-between u-flex-align-center u-gap-medium">
          {renderPolicyOverviewButton()}
          {renderExpandAllButton()}
        </div>
        <Spacer size="x-large" />
      </div>
    )
  }

  if (isLoading) {
    return <ContentLoader testId="privacy-policy-loader" />
  }

  return (
    <div className="policy__content">
      {renderTitleSection()}
      <Text as="article" text={privacyPolicyContent} html />
      <BackToTopButton />
    </div>
  )
}
export default PrivacyPolicy
